import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SavedTrip,
  deleteSavedTrip,
  fetchSavedTrips,
  scheduleSavedTrip,
} from '../../services/atlasApi';
import { ApiError } from '../../services/apiBase';
import { trackSavedTripScheduled } from '../../hooks/useAnalytics';
import './SavedTripsSection.css';

/**
 * Guides kept without dates (spec §22.3).
 *
 * Rendered as its own section rather than merged into the lifecycle "Saved"
 * group, which the spec nominally asks for: that group renders itineraries
 * with multi-select, badges and kebab menus that none of these rows have, and
 * a second row type threaded through it would be a large change to a component
 * this feature does not otherwise touch. The grouping is a presentation
 * detail; the deviation is recorded in the PR rather than made silently.
 *
 * The only action here is the one the phase exists for: give it dates.
 */
const SavedTripsSection: React.FC = () => {
  const navigate = useNavigate();
  const [saved, setSaved] = useState<SavedTrip[] | null>(null);
  const [scheduling, setScheduling] = useState<number | null>(null);
  // Keyed by trip: one shared value pre-filled the next row with a date picked
  // for a different trip, which is a wrong default rather than a convenient one.
  const [dates, setDates] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    fetchSavedTrips()
      .then((res) => setSaved(res.results))
      .catch(() => setSaved([]));
  }, []);

  useEffect(load, [load]);

  const confirmDates = useCallback(async (trip: SavedTrip) => {
    const startDate = dates[trip.id];
    if (!startDate || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await scheduleSavedTrip(trip.id, startDate);
      trackSavedTripScheduled(trip.destination_slug, trip.edition_number);
      navigate(`/plans/trip/${res.itinerary.id}`);
    } catch (err) {
      // A tier limit is the expected failure here, and it needs to say so
      // rather than look like a network blip.
      // Only text the SERVER produced is worth showing: a tier limit's
      // message, a 409's explanation. A transport failure's message is
      // "Failed to fetch", which tells the reader nothing they can act on.
      const api = err instanceof ApiError ? err : null;
      const body = api?.body as Record<string, unknown> | undefined;
      const detail = typeof body?.message === 'string' ? body.message
        : typeof body?.detail === 'string' ? body.detail
        : api && !/^API error: \d+$/.test(api.message) ? api.message
        : '';
      setError(detail || 'Couldn’t set those dates. Try again.');
      setBusy(false);
    }
  }, [dates, busy, navigate]);

  const remove = useCallback(async (trip: SavedTrip) => {
    setSaved((current) => (current || []).filter((t) => t.id !== trip.id));
    try {
      await deleteSavedTrip(trip.id);
    } catch {
      load();
    }
  }, [load]);

  // Nothing saved is not an empty state worth showing — the section simply
  // isn't there until it has something in it.
  if (!saved || saved.length === 0) return null;

  return (
    <div className="sts-section">
      <div className="sts-header">
        <span className="sts-label">SAVED GUIDES</span>
        <span className="sts-count u-timetable">{saved.length}</span>
      </div>

      {saved.map((trip) => (
        <div className="sts-row" key={trip.id}>
          <div className="sts-main">
            <button
              className="sts-title"
              onClick={() => navigate(`/d/${trip.destination_slug}/e/${trip.edition_number}`)}
            >
              {trip.title}
            </button>
            <p className="sts-meta u-timetable">
              {trip.where || trip.destination_name} · {trip.duration_days} days
              {trip.is_customized ? ' · edited' : ''}
            </p>
          </div>

          {scheduling === trip.id ? (
            <div className="sts-dates">
              <label className="sts-date-label" htmlFor={`sts-date-${trip.id}`}>
                Start date
              </label>
              <input
                id={`sts-date-${trip.id}`}
                type="date"
                value={dates[trip.id] || ''}
                onChange={(e) => setDates((d) => ({ ...d, [trip.id]: e.target.value }))}
              />
              <button
                className="sts-confirm"
                onClick={() => confirmDates(trip)}
                disabled={!dates[trip.id] || busy}
              >
                {busy ? 'Setting…' : 'Confirm'}
              </button>
              <button className="sts-cancel" onClick={() => { setScheduling(null); setError(''); }}>
                Cancel
              </button>
              {error && <p className="sts-error" role="status">{error}</p>}
            </div>
          ) : (
            <div className="sts-actions">
              <button className="sts-set" onClick={() => { setScheduling(trip.id); setError(''); }}>
                Set dates
              </button>
              <button
                className="sts-remove"
                onClick={() => remove(trip)}
                aria-label={`Remove ${trip.title}`}
              >
                Remove
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default SavedTripsSection;
